/**
 * Client realtime multiplexé — UNE seule connexion EventSource par onglet.
 *
 * Singleton au niveau module : tous les composants qui s'abonnent (via
 * useRealtimeStream / useRealtimeRefresh) partagent la MÊME connexion SSE vers
 * `GET /realtime/stream?topics=<union>`. Indispensable sous php-fpm où chaque
 * EventSource ouvert monopolise un worker — on n'en veut qu'un par onglet.
 *
 * Responsabilités centralisées ici (jamais dupliquées par feature) :
 *   - union des topics de tous les abonnements → une seule URL de flux ;
 *   - reconnexion manuelle : backoff exponentiel + jitter (on pilote, pas le
 *     navigateur, pour compter les tentatives et basculer en fallback) ;
 *   - Last-Event-ID COMPOSITE (curseur par topic) → rejeu après reconnexion ;
 *   - watchdog heartbeat (reconnecte un socket silencieusement mort) ;
 *   - fallback : après N échecs, "sync" périodique (les abonnés refetchent),
 *     puis reprise SSE dès que possible ;
 *   - authentification par cookie bdl_token (withCredentials).
 *
 * Les reconnexions sont SILENCIEUSES (aucun toast/popup).
 */

import { API_URL } from '@/lib/api';

export interface RealtimeEvent {
  topic: string;
  /** Type métier (ex. "status_changed", "created") ou "sync" (événement synthétique). */
  type: string;
  payload: Record<string, unknown>;
  timestamp?: string | null;
  id?: string;
}

export type RealtimeStatus = 'connecting' | 'open' | 'reconnecting' | 'fallback';

export interface RealtimeSubscription {
  topics: string[];
  onEvent: (event: RealtimeEvent) => void;
  onStatus?: (status: RealtimeStatus) => void;
}

type TimerId = ReturnType<typeof setTimeout>;

const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS = 30000;
const FALLBACK_AFTER = 4; // tentatives échouées avant de basculer en polling
const WATCHDOG_MS = 45000; // pas d'event/heartbeat → socket mort → reconnexion
const FALLBACK_POLL_MS = 30000; // cadence de "sync" en mode fallback
const TOPIC_DEBOUNCE_MS = 120; // regroupe les montages simultanés en une connexion

class RealtimeClient {
  private subs = new Set<RealtimeSubscription>();
  private es: EventSource | null = null;
  private connectedTopics: string[] = [];
  private attempt = 0;
  private lastEventId: string | null = null;
  private status: RealtimeStatus = 'connecting';

  private reconnectTimer: TimerId | null = null;
  private watchdogTimer: TimerId | null = null;
  private fallbackTimer: ReturnType<typeof setInterval> | null = null;
  private topicChangeTimer: TimerId | null = null;

  /** Abonne un consommateur. Renvoie la fonction de désabonnement. */
  subscribe(sub: RealtimeSubscription): () => void {
    this.subs.add(sub);
    sub.onStatus?.(this.status);
    // Déjà connecté : "sync" immédiat pour que le nouveau composant se rafraîchisse.
    if (this.status === 'open') {
      this.emitSync(sub);
    }
    this.reconcileTopics();
    return () => this.unsubscribe(sub);
  }

  private unsubscribe(sub: RealtimeSubscription): void {
    this.subs.delete(sub);
    if (this.subs.size === 0) {
      this.shutdown();
    } else {
      this.reconcileTopics();
    }
  }

  // --- gestion des topics / (re)connexion --------------------------------

  private unionTopics(): string[] {
    const set = new Set<string>();
    for (const sub of this.subs) {
      for (const t of sub.topics) {
        if (t) set.add(t);
      }
    }
    return Array.from(set).sort();
  }

  private reconcileTopics(): void {
    if (typeof window === 'undefined' || typeof EventSource === 'undefined') return;

    const union = this.unionTopics();
    if (union.length === 0) {
      this.shutdown();
      return;
    }
    if (this.es && this.sameSet(union, this.connectedTopics)) {
      return; // rien à changer
    }
    // Debounce : regroupe plusieurs (dé)montages en une seule reconnexion.
    if (this.topicChangeTimer !== null) clearTimeout(this.topicChangeTimer);
    this.topicChangeTimer = setTimeout(() => {
      this.topicChangeTimer = null;
      this.connect(this.unionTopics());
    }, TOPIC_DEBOUNCE_MS);
  }

  private connect(topics: string[]): void {
    if (topics.length === 0) {
      this.shutdown();
      return;
    }
    this.closeEventSource();
    this.connectedTopics = topics;
    this.setStatus(this.attempt === 0 ? 'connecting' : 'reconnecting');

    let url = `${API_URL}/realtime/stream?topics=${encodeURIComponent(topics.join(','))}`;
    if (this.lastEventId) {
      url += `&lastEventId=${encodeURIComponent(this.lastEventId)}`;
    }

    const es = new EventSource(url, { withCredentials: true });
    this.es = es;

    es.onopen = () => this.handleOpen();
    es.onmessage = (ev) => this.handleMessage(ev); // événements réels = message par défaut
    es.addEventListener('heartbeat', () => this.resetWatchdog());
    es.onerror = () => this.handleError();
  }

  private handleOpen(): void {
    this.attempt = 0;
    this.stopFallback();
    this.setStatus('open');
    this.resetWatchdog();
    // Rafraîchit tout le monde à la (re)connexion (couvre la fenêtre de connexion
    // et tout ce qui a changé pendant la coupure).
    for (const sub of this.subs) {
      this.emitSync(sub);
    }
  }

  private handleMessage(ev: MessageEvent): void {
    if (ev.lastEventId) this.lastEventId = ev.lastEventId;
    this.resetWatchdog();

    const event = this.parse(ev.data);
    if (event) this.dispatch(event);
  }

  private handleError(): void {
    if (this.subs.size === 0) return;
    // On pilote la reconnexion : on ferme la source courante pour que le retry
    // natif du navigateur ne coure pas contre notre backoff.
    this.closeEventSource();
    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer !== null || this.subs.size === 0) return;

    this.attempt += 1;
    this.setStatus(this.attempt >= FALLBACK_AFTER ? 'fallback' : 'reconnecting');
    if (this.attempt >= FALLBACK_AFTER) this.startFallback();

    const backoff = Math.min(MAX_DELAY_MS, BASE_DELAY_MS * 2 ** (this.attempt - 1));
    const delay = backoff + Math.floor(Math.random() * BASE_DELAY_MS);

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect(this.unionTopics());
    }, delay);
  }

  // --- watchdog & fallback ------------------------------------------------

  private resetWatchdog(): void {
    if (this.watchdogTimer !== null) clearTimeout(this.watchdogTimer);
    if (this.subs.size === 0) return;
    this.watchdogTimer = setTimeout(() => this.handleError(), WATCHDOG_MS);
  }

  private startFallback(): void {
    if (this.fallbackTimer !== null) return;
    // En fallback : on pousse un "sync" périodique → les abonnés refetchent
    // (équivalent de l'ancien polling 30s) jusqu'au retour du SSE.
    this.fallbackTimer = setInterval(() => {
      for (const sub of this.subs) this.emitSync(sub);
    }, FALLBACK_POLL_MS);
  }

  private stopFallback(): void {
    if (this.fallbackTimer !== null) {
      clearInterval(this.fallbackTimer);
      this.fallbackTimer = null;
    }
  }

  // --- dispatch -----------------------------------------------------------

  private dispatch(event: RealtimeEvent): void {
    for (const sub of this.subs) {
      if (sub.topics.includes(event.topic)) {
        try {
          sub.onEvent(event);
        } catch {
          // un handler défaillant ne doit pas casser le dispatch aux autres
        }
      }
    }
  }

  /** Événement synthétique "sync" (un par topic de l'abonnement). */
  private emitSync(sub: RealtimeSubscription): void {
    for (const topic of sub.topics) {
      try {
        sub.onEvent({ topic, type: 'sync', payload: {} });
      } catch {
        // ignore
      }
    }
  }

  private setStatus(status: RealtimeStatus): void {
    if (status === this.status) return;
    this.status = status;
    for (const sub of this.subs) sub.onStatus?.(status);
  }

  private shutdown(): void {
    this.closeEventSource();
    this.stopFallback();
    if (this.reconnectTimer !== null) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }
    if (this.watchdogTimer !== null) { clearTimeout(this.watchdogTimer); this.watchdogTimer = null; }
    if (this.topicChangeTimer !== null) { clearTimeout(this.topicChangeTimer); this.topicChangeTimer = null; }
    this.connectedTopics = [];
    this.attempt = 0;
  }

  private closeEventSource(): void {
    if (this.es) {
      this.es.onopen = null;
      this.es.onmessage = null;
      this.es.onerror = null;
      this.es.close();
      this.es = null;
    }
  }

  private parse(data: string): RealtimeEvent | null {
    try {
      const obj = JSON.parse(data) as Partial<RealtimeEvent>;
      if (!obj || typeof obj.topic !== 'string' || typeof obj.type !== 'string') return null;
      return {
        topic: obj.topic,
        type: obj.type,
        payload: (obj.payload && typeof obj.payload === 'object' ? obj.payload : {}) as Record<string, unknown>,
        timestamp: typeof obj.timestamp === 'string' ? obj.timestamp : null,
        id: typeof obj.id === 'string' ? obj.id : undefined,
      };
    } catch {
      return null;
    }
  }

  private sameSet(a: string[], b: string[]): boolean {
    if (a.length !== b.length) return false;
    const bset = new Set(b);
    return a.every((x) => bset.has(x));
  }
}

/** Singleton partagé — une seule instance (donc un seul EventSource) par onglet. */
export const realtimeClient = new RealtimeClient();
